import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import './ResendTestPage.css';

interface EmailRecord {
  _id: string;
  direction?: 'sent' | 'received';
  from_email: string;
  to_email: string;
  subject: string;
  content?: string;
  full_content?: string;
  sent_at?: string;
  created_at?: string;
  resend_message_id?: string;
  in_reply_to?: string;
  references?: string;
  attachments?: Array<{
    id?: string;
    filename?: string;
    name?: string;
    size?: number;
    download_url?: string;
  }>;
}

const ResendTestPage: React.FC = () => {
  const { token } = useAuth();
  const [emails, setEmails] = useState<EmailRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    to_email: '',
    subject: 'Test Email from Resend',
    html_content: '<h1>Test Email</h1><p>This is a test email sent via Resend.</p><p><strong>Reply to this email to test threading!</strong></p>',
    from_email: 'onboarding@resend.dev'
  });

  // Fetch emails from MongoDB
  const fetchEmails = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/resend-test/list-emails`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails || []);
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch emails' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Error fetching emails' });
    } finally {
      setLoading(false);
    }
  };

  // Send test email
  const sendEmail = async () => {
    if (!token || !formData.to_email) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/resend-test/send-test-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Email sent successfully!' });
        setFormData({
          ...formData,
          to_email: '',
          subject: 'Re: ' + formData.subject
        });
        // Refresh emails after sending
        setTimeout(() => fetchEmails(), 1000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to send email' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Error sending email' });
    } finally {
      setSending(false);
    }
  };

  // Load emails on mount
  useEffect(() => {
    fetchEmails();
    // Refresh every 5 seconds to see new incoming emails
    const interval = setInterval(fetchEmails, 5000);
    return () => clearInterval(interval);
  }, [token]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const stripHtml = (html?: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').substring(0, 200);
  };

  return (
    <div className="resend-test-page">
      <div className="resend-test-container">
        <h1>📧 Resend Email Test Page</h1>
        <p className="test-description">
          Test Resend email service - Send emails and view received emails with threading support
        </p>

        {/* Message */}
        {message && (
          <div className={`test-message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Send Email Form */}
        <div className="test-section">
          <h2>Send Test Email</h2>
          <div className="test-form">
            <div className="form-group">
              <label>To Email:</label>
              <input
                type="email"
                value={formData.to_email}
                onChange={(e) => setFormData({ ...formData, to_email: e.target.value })}
                placeholder="recipient@example.com"
              />
            </div>
            <div className="form-group">
              <label>From Email:</label>
              <input
                type="email"
                value={formData.from_email}
                onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                placeholder="onboarding@resend.dev"
              />
            </div>
            <div className="form-group">
              <label>Subject:</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>HTML Content:</label>
              <textarea
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                rows={6}
              />
            </div>
            <button
              onClick={sendEmail}
              disabled={sending || !formData.to_email}
              className="send-button"
            >
              {sending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>

        {/* Emails List */}
        <div className="test-section">
          <div className="section-header">
            <h2>Emails ({emails.length})</h2>
            <button onClick={fetchEmails} disabled={loading} className="refresh-button">
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>

          {loading && emails.length === 0 ? (
            <div className="loading">Loading emails...</div>
          ) : emails.length === 0 ? (
            <div className="no-emails">No emails found. Send a test email or wait for incoming emails.</div>
          ) : (
            <div className="emails-list">
              {emails.map((email) => (
                <div
                  key={email._id}
                  className={`email-item ${email.direction || 'unknown'}`}
                >
                  <div className="email-header">
                    <div className="email-direction">
                      {email.direction === 'sent' ? '📤 Sent' : email.direction === 'received' ? '📥 Received' : '📧 Email'}
                    </div>
                    <div className="email-time">{formatDate(email.sent_at || email.created_at)}</div>
                  </div>
                  
                  <div className="email-from-to">
                    <div><strong>From:</strong> {email.from_email}</div>
                    <div><strong>To:</strong> {email.to_email}</div>
                  </div>

                  <div className="email-subject">
                    <strong>Subject:</strong> {email.subject || '(No subject)'}
                  </div>

                  {/* Threading Info */}
                  {(email.in_reply_to || email.references || email.resend_message_id) && (
                    <div className="email-threading">
                      {email.resend_message_id && (
                        <div><strong>Message-ID:</strong> <code>{email.resend_message_id}</code></div>
                      )}
                      {email.in_reply_to && (
                        <div><strong>In-Reply-To:</strong> <code>{email.in_reply_to}</code></div>
                      )}
                      {email.references && (
                        <div><strong>References:</strong> <code>{email.references}</code></div>
                      )}
                    </div>
                  )}

                  {/* Content */}
                  <div className="email-content">
                    {email.full_content ? (
                      <div dangerouslySetInnerHTML={{ __html: email.full_content.substring(0, 500) + '...' }} />
                    ) : (
                      <div>{stripHtml(email.content)}</div>
                    )}
                  </div>

                  {/* Attachments */}
                  {email.attachments && email.attachments.length > 0 && (
                    <div className="email-attachments">
                      <strong>📎 Attachments ({email.attachments.length}):</strong>
                      <div className="attachments-list">
                        {email.attachments.map((att, idx) => (
                          <div key={idx} className="attachment-item">
                            <span>{att.filename || att.name || 'Unknown'}</span>
                            {att.size && <span>({Math.round(att.size / 1024)} KB)</span>}
                            {att.download_url && (
                              <a href={att.download_url} target="_blank" rel="noopener noreferrer">
                                Download
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="test-section instructions">
          <h2>📋 Test Instructions</h2>
          <ol>
            <li>Send a test email using the form above</li>
            <li>Check your email inbox for the message</li>
            <li>Reply to that email</li>
            <li>Check this page - the reply should appear automatically (auto-refresh every 5 seconds)</li>
            <li>Verify threading information (Message-ID, In-Reply-To, References) is displayed</li>
            <li>Test attachments by sending an email with attachments</li>
          </ol>
          <p><strong>Note:</strong> Make sure Resend webhook is configured to: <code>/api/resend-test/receive-email</code></p>
        </div>
      </div>
    </div>
  );
};

export default ResendTestPage;

